import {
    copyItemSucceededNotification,
    copyItemsSucceededNotification,
} from './copyMailListRows.locstring.json';
import loc, { format } from 'owa-localize';
import datapoints from './datapoints';
import copyItemsService from '../services/copyItemsService';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { MailIcons } from 'owa-mail-icons';
import { getListViewNotificationDimensions } from 'owa-mail-layout';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import copyConversationService from 'owa-mail-store/lib/services/copyConversationService';
import getServiceContextFolderId from 'owa-mail-store/lib/services/utils/getServiceContextFolderId';
import { lazyShowNotification } from 'owa-notification-bar';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';

import {
    processApplyConversationActionResponseMessage,
    processMoveOrCopyItemActionResponseMessage,
} from 'owa-mail-triage-action-response-processor';
import {
    getApplyConversationActionContext,
    getRowIdsFromRowKeys,
    TableView,
} from 'owa-mail-list-store';

/**
 * Copy the mail list rows to a destination folder
 * @param rowKeys of the rows to copy
 * @param originalTableView which contains the rows to move from
 * @param destinationFolderId id of the destination folder to move to
 * @param sourceFolderId the actual source folder that contains the rows
 * @param actionSource the action source that triggers the copy action
 */
export default createLegacyActionWithDatapoint('copyMailListRows', datapoints.copyMailListRows)(
    function copyMailListRows(
        rowKeys: string[],
        originalTableView: TableView,
        destinationFolderId: string,
        sourceFolderId: string,
        actionSource: ActionSource
    ): Promise<void> {
        switch (originalTableView.tableQuery.listViewType) {
            case ReactListViewType.Conversation:
                const conversationActionContext = rowKeys.map(rowKey =>
                    getApplyConversationActionContext(rowKey, originalTableView.id)
                );

                const copyConversationPromise = copyConversationService(
                    conversationActionContext,
                    destinationFolderId,
                    sourceFolderId
                );

                showCopyNotification(rowKeys.length);

                return processApplyConversationActionResponseMessage(
                    copyConversationPromise,
                    rowKeys,
                    sourceFolderId,
                    originalTableView,
                    false /* shouldRemoveRowsFromTombstone */
                );

            case ReactListViewType.Message:
                const rowIdStrings = getRowIdsFromRowKeys(rowKeys, originalTableView.id);
                const copyItemServicePromise = copyItemsService(
                    rowIdStrings,
                    getServiceContextFolderId(destinationFolderId),
                    false /* RemoteExecute */,
                    getMailboxInfo(originalTableView).requestOptions
                );

                showCopyNotification(rowKeys.length);

                return processMoveOrCopyItemActionResponseMessage(
                    copyItemServicePromise,
                    rowKeys,
                    sourceFolderId,
                    originalTableView,
                    false /* shouldRemoveRowsFromTombstone */
                );

            default:
                return Promise.reject(
                    new Error(
                        'ListViewType not support: ' + originalTableView.tableQuery.listViewType
                    )
                );
        }
    }
);

function showCopyNotification(numberOfItems: number) {
    const notificationText =
        numberOfItems === 1
            ? loc(copyItemSucceededNotification)
            : format(loc(copyItemsSucceededNotification), numberOfItems);
    const notificationDimensions = getListViewNotificationDimensions();

    lazyShowNotification.importAndExecute(
        'copyNotification',
        'MailModuleNotificationBarHost',
        notificationText,
        {
            icon: MailIcons.Copy,
            minWidth: notificationDimensions.minWidth,
            maxWidth: notificationDimensions.maxWidth,
        }
    );
}
